import { createSlice } from "@reduxjs/toolkit"
import {
  CompletePaymentResponse,
  postEcommOrdersComplete,
  postEcommPaymentRedirectTransaction,
} from "api"
import * as orderRepository from "components/shopFeatures/order/orderRepository"
import { createAsyncAppThunk } from "store"

export interface TransactionParams {
  succeed: string
  uniqueID: string
  lang: string
  authNumber: string
  cgUid: string
  responseMac: string
  cardToken: string
  cardExp: string
  personalId: string
  cardMask: string
  txId: string
  numberOfPayments: string
  firstPayment: string
  periodicalPayment: string
  ErrorCode: string
  ErrorText: string
}

const name = "payment"

type PaymentState = {
  creditGuardUrl: string
  transactionId: string
  transactionComplete: CompletePaymentResponse | null
  outOfStockComplete: boolean
}

export const initialState: PaymentState = {
  creditGuardUrl: "",
  transactionComplete: null,
  transactionId: "",
  outOfStockComplete: false,
}

export const fetchPaymentUrl = createAsyncAppThunk(
  `${name}/fetchPaymentUrl`,
  async (orderId: string) => {
    const { data } = await postEcommPaymentRedirectTransaction({
      orderId,
      redirectTransactionRequest: {
        userData1: "",
        userData2: "",
        userData3: "",
        currency: "ILS",
      },
    })

    return data.body
  }
)

export const setTransaction = createAsyncAppThunk(
  `${name}/setTransaction`,
  async (transaction: TransactionParams, { getState }) => {
    if (!transaction) return null

    // 1. Get the transaction ID from the state
    const {
      payment: { transactionId },
    } = getState()

    // 2. Get the order ID from the local storage
    const orderId = orderRepository.getOrderId(false) as string

    // 3. Send the transaction details to the server
    const {
      data: { body: response },
    } = await postEcommOrdersComplete({
      orderId,
      transactionId,
      txId: transaction.txId,
      errorCode: transaction.ErrorCode,
      cardToken: transaction.cardToken,
      cardExp: transaction.cardExp,
      personalId: transaction.personalId,
      uniqueId: transaction.uniqueID,
      responseMac: transaction.responseMac,
      paymentSucceed: transaction.succeed === "true" ? true : false,
    })

    // 4. Update the response message
    response.message = transaction.ErrorText || response.message

    // 5. Return the response
    return response
  }
)

const payment = createSlice({
  name,
  initialState,
  reducers: {
    clearCreditGuardUrl(state) {
      state.creditGuardUrl = ""
    },
    resetTransactionComplete(state) {
      state.transactionComplete = null
    },
    setOutOfStockComplete(state, { payload }) {
      state.outOfStockComplete = payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentUrl.fulfilled, (state, { payload }) => {
        if (!payload) return
        state.creditGuardUrl = payload.mpiHostPageUrl
        state.transactionId = payload.transactionId
      })
      .addCase(setTransaction.fulfilled, (state, { payload }) => {
        if (!payload) return
        state.transactionComplete = payload
      })
  },
})

export default payment.reducer

export const {
  actions: {
    clearCreditGuardUrl,
    resetTransactionComplete,
    setOutOfStockComplete,
  },
} = payment
